import { useState } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  Button,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { BusinessFilters as Filters } from '../../../types/admin';

interface BusinessFiltersProps {
  filters: Filters;
  onSearchChange: (query: string) => void;
  onFilterChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
}

/**
 * BusinessFilters - Search and filter controls for businesses
 *
 * Features:
 * - Search by business name, owner name, or owner email
 * - Filter by status (active/inactive)
 * - Filter by size (small/medium/large)
 * - Active filter chips display
 * - Clear all filters button
 */
export default function BusinessFilters({
  filters,
  onSearchChange,
  onFilterChange,
  onClearFilters,
}: BusinessFiltersProps) {
  const [searchQuery, setSearchQuery] = useState(filters.search || '');

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onSearchChange(value);
  };

  const handleStatusChange = (value: string) => {
    if (value === 'all') {
      const { status, ...rest } = filters;
      onFilterChange(rest);
    } else {
      onFilterChange({ status: value as 'active' | 'inactive' });
    }
  };

  const handleSizeChange = (value: string) => {
    if (value === 'all') {
      const { size, ...rest } = filters;
      onFilterChange(rest);
    } else {
      onFilterChange({ size: value as 'small' | 'medium' | 'large' });
    }
  };

  const handleRemoveFilter = (filterKey: keyof Filters) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    onFilterChange(newFilters);

    if (filterKey === 'search') {
      setSearchQuery('');
    }
  };

  const activeFilterCount = Object.keys(filters).filter(
    (key) => filters[key as keyof Filters] !== undefined
  ).length;

  const hasActiveFilters = activeFilterCount > 0;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Search and Filters Row */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search by business name, owner name, or email..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={() => handleSearchChange('')}
                  sx={{ minWidth: 'auto', p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />

        {/* Status Filter */}
        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || 'all'}
            onChange={(e) => handleStatusChange(e.target.value)}
            label="Status"
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon fontSize="small" color="action" />
              </InputAdornment>
            }
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        {/* Size Filter */}
        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel>Size</InputLabel>
          <Select
            value={filters.size || 'all'}
            onChange={(e) => handleSizeChange(e.target.value)}
            label="Size"
            startAdornment={
              <InputAdornment position="start">
                <FilterListIcon fontSize="small" color="action" />
              </InputAdornment>
            }
          >
            <MenuItem value="all">All Sizes</MenuItem>
            <MenuItem value="small">Small (1-3)</MenuItem>
            <MenuItem value="medium">Medium (4-10)</MenuItem>
            <MenuItem value="large">Large (11+)</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClearFilters}
            startIcon={<ClearIcon />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </Button>
        )}
      </Stack>

      {/* Active Filters Chips */}
      {hasActiveFilters && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {filters.search && (
            <Chip
              label={`Search: "${filters.search}"`}
              onDelete={() => handleRemoveFilter('search')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {filters.status && (
            <Chip
              label={`Status: ${filters.status}`}
              onDelete={() => handleRemoveFilter('status')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {filters.size && (
            <Chip
              label={`Size: ${filters.size}`}
              onDelete={() => handleRemoveFilter('size')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {filters.created_from && (
            <Chip
              label={`From: ${new Date(filters.created_from).toLocaleDateString()}`}
              onDelete={() => handleRemoveFilter('created_from')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {filters.created_to && (
            <Chip
              label={`To: ${new Date(filters.created_to).toLocaleDateString()}`}
              onDelete={() => handleRemoveFilter('created_to')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      )}
    </Box>
  );
}
